import { Dialog, Button, DialogContent, Typography, AppBar, Toolbar, IconButton } from "@mui/material"
import { useState } from "react"
import { Identifier, RecordContextProvider, useCreate, useDelete, useGetIdentity, useNotify, usePermissions, useRedirect, useTranslate } from "react-admin";
import { useFormContext, useFormState } from "react-hook-form";
import { CreateInvoiceCommand, CreateInvoicePreviewCommand, InvoiceReadModel, InvoiceType } from "../../GeneratedClients/FreeBillApi";
import InvoiceLayout from "../InvoiceLayout";
import { Close } from '@mui/icons-material';
import firstLetterToLowerCase from "../../helpers/firstLetterToLowerCase";
import { useMutation } from "react-query";
import { apiService } from "../../apiService";
import CustomCommandProgress from "../../customStyles/CustomCommandProgress";
import { Permissions } from "../../authentication/Permissions";
import UpgradeDialog from "../../authorization/UpgradeDialog";

interface IProps {
  draftId?: Identifier
  invoiceType: InvoiceType
}

const CreateInvoiceDialog = ({draftId, invoiceType}: IProps) => {
  const { data: identity, isLoading: identityLoading } = useGetIdentity()
  const [open, setOpen] = useState<boolean>(false)
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState<boolean>(false)
  const [preview, setPreview] = useState<InvoiceReadModel>()
  const { getValues } = useFormContext()
  const { isValid } = useFormState()
  const { isLoading: permissionsLoading, permissions } = usePermissions<Permissions>()
  const [create, {isLoading: isCreatingInvoice}] = useCreate()
  const { mutate, isLoading: isLoadingPreview } = useMutation((command: CreateInvoicePreviewCommand) => 
    apiService.InvoicesApi.createInvoicePreview({createInvoicePreviewCommand: command})  
  )
  const [deleteOne] = useDelete()
  const notify = useNotify()
  const redirect = useRedirect()
  const translate = useTranslate()

  if(identityLoading || permissionsLoading) return null

  const handleOpen = () => {
    if(isValid && !permissions.createInvoice) {
      setOpenUpgradeDialog(true)
    }
    if(isValid && permissions.createInvoice) {
      const command: CreateInvoicePreviewCommand = getValues()
      command.userId = identity?.id.toString()
      mutate(command, {
        onSuccess: (data) => { 
          setPreview(data.data)
          setOpen(true)
        },
        onError: (error: any) => { 
          var message = error?.response?.data?.errors?.UserId[0] ? error?.response?.data?.errors?.UserId[0] : error
          notify(`${message}`, {type: 'error'}) 
        }
      })
    }
  }

  const handleClose = () => {
    setOpen(false)
  }
  
  const handleSave = () => {
    const data: CreateInvoiceCommand = getValues()
    data.userId = identity?.id.toString()
    create('invoices', {data}, {
      onSuccess: (data) => { 
        draftId && deleteOne("drafts", {id: draftId})
        notify(translate('resources.invoices.notifications.created')) 
        redirect('list', 'invoices')
      },
      onError: (error) => { 
        notify(`${error}`, {type: 'error'}) 
      }
    })
  }

  return (
    <>
      <Button 
        onClick={handleOpen} 
        variant={'contained'} 
        sx={{mr: 2}}
        size="small"
        type="submit"
        disabled={isLoadingPreview}
      >
        {translate('resources.invoices.actions.create')}
      </Button>
      <UpgradeDialog 
        open={openUpgradeDialog}
        setOpen={setOpenUpgradeDialog}
        title="Rechnungs-Limit pro Monat erreicht"
        content="Du hast diesen Monat bereits 3 von 3 Rechnungen geschrieben."
      />
      <Dialog 
        open={open}
        onClose={handleClose}
        fullScreen
      >
        <AppBar sx={{position: 'relative'}}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} component={'div'} variant="h6" color={"inherit"}>
              {translate("resources.invoices.dialogs.create", { invoiceType: translate(`resources.invoiceTypes.${firstLetterToLowerCase(invoiceType)}`)})}
            </Typography>
            <Button variant="contained" color="success" onClick={handleSave} autoFocus disabled={isCreatingInvoice} >{translate('ra.action.create')}</Button>
          </Toolbar>
        </AppBar>
        <DialogContent >
          { (isCreatingInvoice || isLoadingPreview) && <CustomCommandProgress /> }
          <RecordContextProvider value={preview} >
              <InvoiceLayout resource="invoices" isPreview={true} />
          </RecordContextProvider>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreateInvoiceDialog